import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { FiSend, FiUser, FiFolder, FiCalendar, FiClock, FiFileText, FiPrinter, FiEdit2, FiEye, FiTrash2 } from 'react-icons/fi';
import Drawer from '../../components/ui/Drawer';
import Button from '../../components/ui/Button';
import StatusPill from '../../components/ui/StatusPill';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import ErrorBanner from '../../components/ui/ErrorBanner';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import CurrencyDisplay from '../../components/shared/CurrencyDisplay';
import DateDisplay from '../../components/shared/DateDisplay';
import { getInvoice, updateInvoice, downloadInvoicePdf, downloadInvoiceDocx, getInvoicePdfUrl, deleteInvoice } from '../../api/invoices';
import { listCustomers } from '../../api/customers';
import { listDocuments, viewDocumentBlobUrl } from '../../api/documents';
import { extractErrorMessage, api } from '../../api/client';
import { InvoiceStatus } from '../../enums';
import { useAuth } from '../../auth/AuthContext';
import Modal from '../../components/ui/Modal';
import InvoiceFormInner from './InvoiceFormInner';
import { emptyItem } from './InvoiceItemsEditor';
import { toIsoDate } from '../../utils/date';

function MetaItem({ icon: Icon, label, children }) {
  return (
    <div className="flex items-start gap-3">
      <div className="mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-md bg-slate-100 text-ink-muted">
        <Icon size={14} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[10px] font-medium uppercase tracking-wide text-ink-muted">
          {label}
        </div>
        <div className="mt-0.5 text-sm text-ink">{children}</div>
      </div>
    </div>
  );
}

function SummaryRow({ label, value, tone = 'default', big = false }) {
  const toneCls =
    tone === 'success'
      ? 'text-success'
      : tone === 'warning'
        ? 'text-warning'
        : tone === 'primary'
          ? 'text-primary'
          : 'text-ink';
  return (
    <div className="flex items-center justify-between py-2">
      <span className="text-xs font-medium uppercase tracking-wide text-ink-muted">
        {label}
      </span>
      <span
        className={`${big ? 'text-lg font-semibold' : 'text-sm font-medium'} ${toneCls}`}
      >
        {value}
      </span>
    </div>
  );
}

const numberToWords = (num, currency = 'INR') => {
  const cName = currency === 'INR' ? 'Rupees' : currency === 'USD' ? 'Dollars' : currency === 'EUR' ? 'Euros' : currency === 'GBP' ? 'Pounds' : currency;
  if (!num || isNaN(num) || num === 0) return `Zero ${cName} only`;
  num = Math.floor(num);
  const a = ['', 'One ', 'Two ', 'Three ', 'Four ', 'Five ', 'Six ', 'Seven ', 'Eight ', 'Nine ', 'Ten ', 'Eleven ', 'Twelve ', 'Thirteen ', 'Fourteen ', 'Fifteen ', 'Sixteen ', 'Seventeen ', 'Eighteen ', 'Nineteen '];
  const b = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
  
  const n = ('000000000' + num).substr(-9).match(/^(\d{2})(\d{2})(\d{2})(\d{1})(\d{2})$/);
  if (!n) return '';
  let str = '';
  str += (n[1] !== '00') ? (a[Number(n[1])] || b[n[1][0]] + ' ' + a[n[1][1]]) + 'Crore ' : '';
  str += (n[2] !== '00') ? (a[Number(n[2])] || b[n[2][0]] + ' ' + a[n[2][1]]) + 'Lakh ' : '';
  str += (n[3] !== '00') ? (a[Number(n[3])] || b[n[3][0]] + ' ' + a[n[3][1]]) + 'Thousand ' : '';
  str += (n[4] !== '0') ? (a[Number(n[4])] || b[n[4][0]] + ' ' + a[n[4][1]]) + 'Hundred ' : '';
  str += (n[5] !== '00') ? ((str !== '') ? 'and ' : '') + (a[Number(n[5])] || b[n[5][0]] + ' ' + a[n[5][1]]) : '';
  return str.trim() + ` ${cName} only`;
};

export default function InvoiceDetailDrawer({ open, invoiceId, onClose, fallback }) {
  const qc = useQueryClient();
  const { isSuperadmin } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [editErrors, setEditErrors] = useState({});
  const [isPdfLoading, setIsPdfLoading] = useState(false);
  const [isDocxLoading, setIsDocxLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isExternalPreviewLoading, setIsExternalPreviewLoading] = useState(false);
  const [downloadMenuOpen, setDownloadMenuOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const handleDownloadPdf = async () => {
    if (!invoiceId) return;
    try {
      setIsPdfLoading(true);
      await downloadInvoicePdf(invoiceId, `invoice_${invoice?.invoiceNumber || invoiceId}.pdf`);
    } catch (err) {
      console.error(err);
      toast.error('Failed to download PDF');
    } finally {
      setIsPdfLoading(false);
    }
  };

  const handleDownloadDocx = async () => {
    if (!invoiceId) return;
    try {
      setIsDocxLoading(true);
      await downloadInvoiceDocx(invoiceId, `invoice_${invoice?.invoiceNumber || invoiceId}.docx`);
    } catch (err) {
      console.error(err);
      toast.error('Failed to download DOCX');
    } finally {
      setIsDocxLoading(false);
    }
  };

  const handlePreviewPdf = async () => {
    if (!invoiceId) return;
    try {
      setIsPdfLoading(true);
      const url = await getInvoicePdfUrl(invoiceId);
      setPreviewUrl(url);
    } catch (err) {
      console.error(err);
      toast.error('Failed to open preview');
    } finally {
      setIsPdfLoading(false);
    }
  };

  const {
    data: fetched,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['invoices', invoiceId],
    queryFn: () => getInvoice(invoiceId),
    enabled: !!invoiceId && open,
    initialData: fallback && fallback.id === invoiceId ? fallback : undefined,
  });

  const invoice = fetched || (fallback?.id === invoiceId ? fallback : null);

  const { data: products = [] } = useQuery({
    queryKey: ['products', 'list-simple'],
    queryFn: async () => {
      const res = await api.get('/product/getAllProducts');
      const data = res.data || [];
      return data.map(p => ({ id: p.id, name: p.productname || p.productName }));
    },
    enabled: isEditing,
  });

  const { data: customers = [] } = useQuery({
    queryKey: ['customers', 'all'],
    queryFn: listCustomers,
    enabled: isEditing,
  });

  React.useEffect(() => {
    if (!open) {
      setIsEditing(false);
    }
  }, [open]);

  React.useEffect(() => {
    if (invoice && isEditing) {
      let matchedCustomerId = invoice.customer?.id || invoice.customer?.customerId || invoice.customerId || invoice.client?.id || invoice.clientId || null;
      
      if (!matchedCustomerId && customers.length > 0) {
        const match = customers.find(c => {
          const fullName = [c.firstName, c.lastName].filter(Boolean).join(' ');
          return (
            (c.companyName && c.companyName === invoice.customerName) ||
            (c.companyName && c.companyName === invoice.clientName) ||
            (fullName && fullName === invoice.customerName) ||
            (fullName && fullName === invoice.clientName)
          );
        });
        if (match) {
          matchedCustomerId = match.id;
        }
      }

      setEditForm({
        customerId: matchedCustomerId,
        productId: invoice.productId || '',
        invoiceType: invoice.invoiceType || '',
        invoiceDate: toIsoDate(invoice.invoiceDate) || '',
        dueDate: toIsoDate(invoice.dueDate) || '',
        notes: invoice.notes || '',
        items: invoice.items?.length ? invoice.items.map(it => ({...it})) : [emptyItem()],
        isGst: invoice.isGst ?? true,
        currency: invoice.currency || 'INR',
        billingCountry: invoice.billingCountry || 'IN',
        poNumber: invoice.poNumber || '',
        contactPerson: invoice.contactPerson || '',
        createdByName: invoice.createdByName || '',
      });
      setEditErrors({});
    }
  }, [invoice, isEditing, customers]);

  const updateMutation = useMutation({
    mutationFn: (payload) => updateInvoice(invoiceId, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invoices'] });
      qc.invalidateQueries({ queryKey: ['invoices', invoiceId] });
      toast.success('Invoice updated successfully');
      setIsEditing(false);
    },
    onError: (e) => toast.error(extractErrorMessage(e)),
  });

  function validateEdit() {
    const errs = {};
    if (!editForm.customerId) errs.customerId = 'Customer is required';
    if (!editForm.dueDate) errs.dueDate = 'Due date is required';
    if (!editForm.items?.length) errs.items = 'Add at least one item';
    const itemErrs = {};
    editForm.items?.forEach((it, i) => {
      const r = {};
      if (!it.name || !String(it.name).trim()) r.name = 'Required';
      const q = Number(it.quantity);
      if (!q || q <= 0) r.quantity = 'Must be > 0';
      const p = Number(it.unitPrice);
      if (Number.isNaN(p) || p < 0) r.unitPrice = 'Invalid';
      if (Object.keys(r).length) itemErrs[i] = r;
    });
    if (Object.keys(itemErrs).length) errs.itemRows = itemErrs;
    setEditErrors(errs);
    if (Object.keys(errs).length > 0) {
      toast.error('Please fix the errors before saving');
      return false;
    }
    return true;
  }

  const deleteMutation = useMutation({
    mutationFn: () => deleteInvoice(invoiceId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invoices'] });
      toast.success('Invoice deleted');
      setDeleteConfirmOpen(false);
      onClose?.();
    },
    onError: (e) => toast.error(extractErrorMessage(e)),
  });

  function handleSaveEdit() {
    if (!validateEdit()) return;
    const payload = {
      customerId: Number(editForm.customerId),
      ...(editForm.productId !== '' && editForm.productId != null ? { productId: Number(editForm.productId) } : {}),
      ...(editForm.invoiceType ? { invoiceType: editForm.invoiceType } : {}),
      ...(editForm.invoiceDate ? { invoiceDate: editForm.invoiceDate } : {}),
      dueDate: editForm.dueDate,
      isGst: editForm.isGst,
      currency: editForm.currency || 'INR',
      billingCountry: editForm.billingCountry || 'IN',
      ...(editForm.notes ? { notes: editForm.notes } : {}),
      ...(editForm.poNumber ? { poNumber: editForm.poNumber } : {}),
      ...(editForm.contactPerson ? { contactPerson: editForm.contactPerson } : {}),
      ...(editForm.createdByName ? { createdByName: editForm.createdByName } : {}),
      items: editForm.items.map((it) => ({
        ...(it.id ? { id: it.id } : {}),
        name: String(it.name || '').trim(),
        ...(it.description ? { description: it.description } : {}),
        ...(it.unit ? { unit: it.unit } : {}),
        quantity: Number(it.quantity) || 0,
        unitPrice: Number(it.unitPrice) || 0,
        taxRate: Number(it.taxRate) || 0,
      })),
    };
    updateMutation.mutate(payload);
  }




  const handlePreviewExternalFile = async () => {
    if (!invoiceId) return;
    try {
      setIsExternalPreviewLoading(true);
      const docs = await listDocuments('INVOICE', invoiceId);
      if (!docs || docs.length === 0) {
        toast.error('No uploaded file found for this invoice');
        return;
      }
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      const blobUrl = await viewDocumentBlobUrl(docs[0].id);
      setPreviewUrl(blobUrl);
    } catch (err) {
      console.error(err);
      toast.error('Failed to open invoice file');
    } finally {
      setIsExternalPreviewLoading(false);
    }
  };
  const isExternal = invoice?.source === 'EXTERNAL';
  const isDraft = invoice?.status === InvoiceStatus.DRAFT;
  const pendingAmount =
    invoice != null
      ? Math.max(0, Number(invoice.totalAmount || 0) - Number(invoice.paidAmount || 0))
      : 0;

  return (
    <>
      <Drawer
        open={open}
        onClose={onClose}
        width="lg"
        title={
          invoice
            ? `Invoice ${invoice.invoiceNumber || `#${invoice.id}`}`
            : 'Invoice'
        }
        footer={
          invoice ? (
            isEditing ? (
              <div className="flex w-full items-center justify-end gap-2">
                <Button variant="ghost" onClick={() => setIsEditing(false)} disabled={updateMutation.isPending}>
                  Cancel
                </Button>
                <Button variant="primary" loading={updateMutation.isPending} onClick={handleSaveEdit}>
                  Save Changes
                </Button>
              </div>
            ) : (
              <div className="flex w-full items-center justify-end gap-2 print-hidden">
                {!isExternal && (
                  <Button variant="outline" icon={FiEye} onClick={handlePreviewPdf} loading={isPdfLoading}>
                    Preview Invoice
                  </Button>
                )}
                {!isExternal && (
                  <div className="relative">
                    <Button variant="outline" onClick={() => setDownloadMenuOpen(!downloadMenuOpen)}>
                      Download Invoice
                    </Button>
                    {downloadMenuOpen && (
                      <>
                        <div className="fixed inset-0 z-40" onClick={() => setDownloadMenuOpen(false)} />
                        <div className="absolute right-0 bottom-full mb-2 w-48 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-50 overflow-hidden">
                          <div className="py-1">
                            <button
                              className="flex w-full items-center px-4 py-2 text-sm text-ink hover:bg-slate-50"
                              onClick={() => { setDownloadMenuOpen(false); handleDownloadPdf(); }}
                            >
                              <FiPrinter className="mr-3 text-ink-muted" size={16} />
                              Download as PDF
                            </button>
                            <button
                              className="flex w-full items-center px-4 py-2 text-sm text-ink hover:bg-slate-50"
                              onClick={() => { setDownloadMenuOpen(false); handleDownloadDocx(); }}
                            >
                              <FiFileText className="mr-3 text-ink-muted" size={16} />
                              Download as Word
                            </button>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                )}
                {isDraft && !isExternal && (
                  <Button
                    variant="outline"
                    icon={FiEdit2}
                    onClick={() => setIsEditing(true)}
                  >
                    Edit
                  </Button>
                )}
                {isSuperadmin && (
                  <Button
                    variant="outline"
                    icon={FiTrash2}
                    onClick={() => setDeleteConfirmOpen(true)}
                    className="text-red-600 border-red-300 hover:bg-red-50 hover:border-red-400"
                    loading={deleteMutation.isPending}
                  >
                    Delete
                  </Button>
                )}
              </div>
            )
          ) : null
        }
      >
        <div className="print-hidden">
          {isLoading && !invoice ? (
            <LoadingSpinner label="Loading invoice..." />
          ) : error && !invoice ? (
            <ErrorBanner error={error} title="Failed to load invoice" onRetry={refetch} />
          ) : !invoice ? (
            <div className="py-10 text-center text-sm text-ink-muted">
              Invoice not found.
            </div>
          ) : isEditing ? (
            <InvoiceFormInner
              form={editForm}
              update={(patch) => setEditForm(f => ({ ...f, ...patch }))}
              errors={editErrors}
              products={products}
              isEditing={true}
            />
          ) : (
            <div className="space-y-6">
              <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-gradient-to-br from-primary/5 to-accent/5 p-4">
                <div>
                  {!isExternal && invoice.totalAmount > 0 && (
                    <p className="text-[13px] font-medium text-slate-900 mt-1 capitalize">{numberToWords(invoice.totalAmount, invoice.currency)}</p>
                  )}
                  <div className="text-[10px] font-medium uppercase tracking-wide text-ink-muted">
                    Invoice Number
                  </div>
                  <div className="text-lg font-semibold text-ink">
                    {invoice.invoiceNumber || `#${invoice.id}`}
                  </div>
                </div>
                <StatusPill value={invoice.status} />
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <MetaItem icon={FiUser} label="Customer">
                  {invoice.customerName || (invoice.customerId ? `#${invoice.customerId}` : '—')}
                </MetaItem>
                {(!isExternal || invoice.productName || invoice.productId) && (
                  <MetaItem icon={FiFolder} label="Product">
                    {invoice.productName || (invoice.productId ? `#${invoice.productId}` : '—')}
                  </MetaItem>
                )}
                <MetaItem icon={FiCalendar} label="Issued">
                  <DateDisplay value={invoice.createdAt} />
                </MetaItem>
                {(!isExternal || invoice.dueDate) && (
                  <MetaItem icon={FiClock} label="Due">
                    <DateDisplay value={invoice.dueDate} />
                  </MetaItem>
                )}
                {invoice.poNumber && (
                  <MetaItem icon={FiFileText} label="PO Number">
                    {invoice.poNumber}
                  </MetaItem>
                )}
                {invoice.contactPerson && (
                  <MetaItem icon={FiUser} label="Contact Person">
                    {invoice.contactPerson}
                  </MetaItem>
                )}
                <MetaItem icon={FiUser} label="Created By">
                  {invoice.createdByName || invoice.createdByUsername || invoice.createdBy || '—'}
                </MetaItem>
                {invoice.notes && (
                  <MetaItem icon={FiFileText} label="Notes">
                    <span className="whitespace-pre-line text-ink-muted">
                      {invoice.notes}
                    </span>
                  </MetaItem>
                )}
              </div>

              {/* Line Items — hidden for external invoices */}
              {!isExternal && (
                <section>
                  <div className="mb-2 flex items-baseline justify-between">
                    <h3 className="text-sm font-semibold text-ink">Line Items</h3>
                    <span className="text-xs text-ink-muted">
                      {invoice.items?.length || 0} item
                      {(invoice.items?.length || 0) === 1 ? '' : 's'}
                    </span>
                  </div>
                  <div className="overflow-x-auto rounded-lg border border-slate-200">
                    <table className="min-w-full divide-y divide-slate-200 text-sm">
                      <thead className="bg-slate-50 text-left text-[10px] font-semibold uppercase tracking-wider text-ink-muted">
                        <tr>
                          <th className="px-3 py-2">Name</th>
                          <th className="px-3 py-2 text-right">Qty</th>
                          <th className="px-3 py-2 text-right">Unit Price</th>
                          <th className="px-3 py-2 text-right">Tax %</th>
                          <th className="px-3 py-2 text-right">Total</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {(invoice.items || []).map((it, i) => {
                          const qty = Number(it.quantity) || 0;
                          const price = Number(it.unitPrice) || 0;
                          const tax = Number(it.taxRate) || 0;
                          const lineTotal = qty * price * (1 + tax / 100);
                          return (
                            <tr key={it.id ?? i}>
                              <td className="px-3 py-2 align-top">
                                <div className="font-medium text-ink">{it.name}</div>
                                {it.description && (
                                  <div className="text-xs text-ink-muted">
                                    {it.description}
                                  </div>
                                )}
                                {it.unit && (
                                  <div className="text-[10px] uppercase tracking-wide text-ink-faint">
                                    {it.unit}
                                  </div>
                                )}
                              </td>
                              <td className="px-3 py-2 text-right tabular-nums">{qty}</td>
                              <td className="px-3 py-2 text-right">
                                <CurrencyDisplay value={price} currency={invoice.currency} />
                              </td>
                              <td className="px-3 py-2 text-right tabular-nums">{tax}%</td>
                              <td className="px-3 py-2 text-right font-medium">
                                <CurrencyDisplay value={lineTotal} currency={invoice.currency} />
                              </td>
                            </tr>
                          );
                        })}
                        {(!invoice.items || invoice.items.length === 0) && (
                          <tr>
                            <td
                              colSpan={5}
                              className="px-3 py-6 text-center text-xs text-ink-muted"
                            >
                              No line items.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </section>
              )}

              {/* Payment Summary — hidden for external invoices */}
              {!isExternal && (
                <section className="rounded-lg border border-slate-200 bg-white">
                  <div className="border-b border-slate-100 px-4 py-3">
                    <h3 className="text-sm font-semibold text-ink">Payment Summary</h3>
                  </div>
                  <div className="divide-y divide-slate-100 px-4">
                    <SummaryRow
                      label="Subtotal"
                      value={<CurrencyDisplay value={invoice.subtotal} currency={invoice.currency} />}
                    />
                    <SummaryRow
                      label="Tax"
                      value={<CurrencyDisplay value={invoice.taxAmount} currency={invoice.currency} />}
                    />
                    <SummaryRow
                      label="Total"
                      big
                      tone="primary"
                      value={<CurrencyDisplay value={invoice.totalAmount} currency={invoice.currency} />}
                    />
                    <SummaryRow
                      label="Paid"
                      tone="success"
                      value={<CurrencyDisplay value={invoice.paidAmount} currency={invoice.currency} />}
                    />
                    <SummaryRow
                      label="Pending"
                      tone={pendingAmount > 0 ? 'warning' : 'success'}
                      value={<CurrencyDisplay value={pendingAmount} currency={invoice.currency} />}
                    />
                    <div className="flex items-center justify-between py-2">
                      <span className="text-xs font-medium uppercase tracking-wide text-ink-muted">
                        Status
                      </span>
                      <StatusPill value={invoice.status} />
                    </div>
                  </div>
                </section>
              )}
            </div>
          )}
        </div>


      </Drawer>

      <Modal
        open={!!previewUrl}
        onClose={() => { if (previewUrl) URL.revokeObjectURL(previewUrl); setPreviewUrl(null); }}
        title={`Invoice Preview - ${invoice?.invoiceNumber || invoiceId}`}
        size="xl"
        noPadding={true}
      >
        {previewUrl && (
          <iframe
            src={`${previewUrl}#toolbar=0&navpanes=0&scrollbar=0`}
            className="w-full min-h-[75vh] border-0 rounded"
            title="Invoice Preview"
          />
        )}
      </Modal>

      <ConfirmDialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={() => deleteMutation.mutate()}
        title="Delete Invoice"
        message={`Are you sure you want to permanently delete invoice ${invoice?.invoiceNumber || `#${invoiceId}`}? This action cannot be undone.`}
        confirmLabel="Delete"
        variant="danger"
        loading={deleteMutation.isPending}
      />
    </>
  );
}
